import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Admin-only workflow triage endpoints backing the hidden `/workflow-admin`
 * page. Built for incident response: find runaway fanout trees, terminate a
 * WHOLE tree (root + abandoned children — a plain run cancel only stops the
 * parent), and bulk-manage scheduled snooze reruns.
 */

export const WorkflowAdminStatusCountSchema = z.object({
  status: z.string(),
  count: z.number().int(),
});

/**
 * Shared scope for the triage reads.
 *
 * Code-owned (`isSystemManaged`) workflows are excluded by default here for the
 * same reason the runs list excludes them: the Platform Heartbeat canary beats
 * every 5 minutes forever, so its runs inflate the status cards without ever
 * being the incident anyone opened this page about. Kept opt-in-able because
 * "the canary itself is wedged" is a real incident.
 */
export const WorkflowAdminScopeInputSchema = z.object({
  includeSystemManaged: z.boolean().default(false),
});

export const WorkflowAdminOverviewOutputSchema = z.object({
  /** Run counts per status for the caller's organization. */
  statusCounts: z.array(WorkflowAdminStatusCountSchema),
  /** Runs currently consuming worker capacity (running / queued / idle). */
  activeRunCount: z.number().int(),
  /** Future snooze reruns waiting on their start delay. */
  scheduledRerunCount: z.number().int(),
  /** Number of independent workflow trees currently active. */
  activeTreeCount: z.number().int(),
  /** Size of the largest active workflow tree (run count). */
  largestTreeSize: z.number().int(),
});

export const WorkflowAdminActiveTreeSchema = z.object({
  /** Root WorkflowRun id — the Temporal workflow id prefix for the tree. */
  rootRunId: z.uuid(),
  /** Temporal workflow id of the root execution. */
  temporalWorkflowId: z.string(),
  workflowId: z.uuid(),
  workflowName: z.string(),
  rootStatus: z.string(),
  rootStopReason: z.string().nullable(),
  triggeredAt: z.iso.datetime(),
  /** Active (running/queued/idle) runs in the tree, including the root. */
  activeRunCount: z.number().int(),
  /** Most recent update across the tree's active runs. */
  latestActivityAt: z.iso.datetime().nullable(),
});

export const ListActiveRunTreesInputSchema = z.object({
  limit: z.number().int().min(1).max(200).default(50),
  includeSystemManaged: z.boolean().default(false),
});

export const ListActiveRunTreesOutputSchema = z.object({
  trees: z.array(WorkflowAdminActiveTreeSchema),
  /** Total active runs org-wide (may exceed the runs in the listed trees). */
  totalActiveRuns: z.number().int(),
});

export const TerminateRunTreeInputSchema = z.object({
  /** Any run id in the tree — resolved up to the root before terminating. */
  workflowRunId: z.uuid(),
  reason: z.string().min(3).max(500),
  /**
   * Also cancel scheduled snooze reruns whose root original is in this tree,
   * so the tree doesn't resurrect itself tomorrow. Defaults to true.
   */
  cancelScheduledReruns: z.boolean().default(true),
});

const TemporalTerminateCountsSchema = z.object({
  /** Temporal executions successfully told to terminate. */
  temporalTerminated: z.number().int(),
  /** Temporal executions already gone (closed or never existed). */
  temporalNotFound: z.number().int(),
  /** Temporal terminate calls that errored (transport/auth) — retry these. */
  temporalFailed: z.number().int(),
  /** True when Temporal is disabled in this environment (DB-only cleanup). */
  temporalSkipped: z.boolean(),
  /** DB rows flipped from an active status to `terminated`. */
  dbRunsMarkedTerminated: z.number().int(),
  /** Scheduled snooze reruns cancelled alongside the terminate. */
  scheduledRerunsCancelled: z.number().int(),
});

export const TerminateRunTreeOutputSchema =
  TemporalTerminateCountsSchema.extend({
    rootRunId: z.uuid(),
    /** Every run row found in the tree (root + all descendants). */
    totalRunsInTree: z.number().int(),
  });

export const WorkflowAdminScheduledRerunSchema = z.object({
  runId: z.uuid(),
  workflowId: z.uuid(),
  workflowName: z.string(),
  scheduledFor: z.iso.datetime().nullable(),
  /** The snooze reason recorded when the rerun was scheduled. */
  reason: z.string().nullable(),
  /** Snooze-chain depth (1 = first automatic rerun of the original). */
  rerunAttempt: z.number().int().nullable(),
  /** The original run this rerun re-executes. */
  rerunOfWorkflowRunId: z.uuid().nullable(),
});

export const ListScheduledRerunsInputSchema = z.object({
  limit: z.number().int().min(1).max(500).default(100),
  includeSystemManaged: z.boolean().default(false),
});

export const ListScheduledRerunsOutputSchema = z.object({
  reruns: z.array(WorkflowAdminScheduledRerunSchema),
  totalScheduled: z.number().int(),
});

export const CancelScheduledRerunsInputSchema = z.object({
  workflowRunIds: z.array(z.uuid()).min(1).max(500),
  reason: z.string().min(3).max(500),
});

export const CancelScheduledRerunsOutputSchema = z.object({
  cancelled: z.number().int(),
  /** Runs that were no longer in `scheduled` status (fired or already gone). */
  alreadyResolved: z.number().int(),
  /**
   * Cancels that errored (e.g. Temporal transport failure after the DB flip).
   * Non-zero means the operator should retry or check Temporal directly.
   */
  failed: z.number().int(),
});

export const TerminateAllActiveInputSchema = z.object({
  reason: z.string().min(3).max(500),
  /**
   * When false (default), code-owned workflows (e.g. the Platform Heartbeat
   * canary) are left running. Flip on only when those are part of the incident.
   */
  includeSystemManaged: z.boolean().default(false),
  /**
   * Also cancel every scheduled snooze rerun in the same scope so nothing
   * resurrects after the kill. Defaults to true.
   */
  cancelScheduledReruns: z.boolean().default(true),
});

export const TerminateAllActiveOutputSchema =
  TemporalTerminateCountsSchema.extend({
    /** Active (running/queued/idle) runs considered for terminate. */
    activeRunsConsidered: z.number().int(),
  });

export type WorkflowAdminOverviewOutput = z.infer<
  typeof WorkflowAdminOverviewOutputSchema
>;
export type WorkflowAdminActiveTree = z.infer<
  typeof WorkflowAdminActiveTreeSchema
>;
export type ListActiveRunTreesOutput = z.infer<
  typeof ListActiveRunTreesOutputSchema
>;
export type TerminateRunTreeInput = z.infer<typeof TerminateRunTreeInputSchema>;
export type TerminateRunTreeOutput = z.infer<
  typeof TerminateRunTreeOutputSchema
>;
export type WorkflowAdminScheduledRerun = z.infer<
  typeof WorkflowAdminScheduledRerunSchema
>;
export type ListScheduledRerunsOutput = z.infer<
  typeof ListScheduledRerunsOutputSchema
>;
export type CancelScheduledRerunsOutput = z.infer<
  typeof CancelScheduledRerunsOutputSchema
>;
export type TerminateAllActiveInput = z.infer<
  typeof TerminateAllActiveInputSchema
>;
export type TerminateAllActiveOutput = z.infer<
  typeof TerminateAllActiveOutputSchema
>;

export const workflowAdminContracts = {
  /** Org-wide run status counts for the triage overview cards. */
  getOverview: oc
    .input(WorkflowAdminScopeInputSchema)
    .output(WorkflowAdminOverviewOutputSchema)
    .route({
      summary: "Get workflow run triage overview",
      description:
        "Admin triage: org-wide workflow run counts by status, active and scheduled-rerun totals, and the number and largest size of active run trees. System workflows excluded by default.",
      method: "GET",
    }),

  /**
   * Active run trees, largest first. Groups every active run under its ROOT
   * run so a 500-child fanout shows as one row with `activeRunCount: 500`
   * instead of 500 anonymous rows.
   */
  listActiveRunTrees: oc
    .input(ListActiveRunTreesInputSchema)
    .output(ListActiveRunTreesOutputSchema)
    .route({
      summary: "List active workflow run trees",
      description:
        "Admin triage: groups active runs under their root run, largest trees first, with workflow name, root status, active run count and latest activity.",
      method: "GET",
    }),

  /**
   * Emergency stop for a whole run tree: terminates the root AND every
   * descendant in Temporal (fanout children are started with
   * ParentClosePolicy.ABANDON, so cancelling only the parent leaves them
   * running), then marks the DB rows terminated.
   */
  terminateRunTree: oc
    .input(TerminateRunTreeInputSchema)
    .output(TerminateRunTreeOutputSchema)
    .route({
      summary: "Terminate an entire workflow run tree",
      description:
        "Emergency stop: terminates the root run and every descendant run, marks them terminated with a reason, and by default cancels the tree's scheduled reruns. Returns counts.",
      method: "POST",
    }),

  /** Upcoming scheduled snooze reruns, soonest first. */
  listScheduledReruns: oc
    .input(ListScheduledRerunsInputSchema)
    .output(ListScheduledRerunsOutputSchema)
    .route({
      summary: "List scheduled workflow reruns",
      description:
        "Admin triage: upcoming snoozed workflow reruns, soonest first, with workflow, scheduled time, snooze reason, attempt number and the original run.",
      method: "GET",
    }),

  /** Bulk-cancel scheduled reruns before their start delay fires. */
  cancelScheduledReruns: oc
    .input(CancelScheduledRerunsInputSchema)
    .output(CancelScheduledRerunsOutputSchema)
    .route({
      summary: "Cancel scheduled workflow reruns",
      description:
        "Bulk-cancels up to 500 scheduled snooze reruns by run ID with a reason before they fire; reports cancelled, already-resolved and failed counts.",
      method: "POST",
    }),

  /**
   * Emergency stop for the whole org: terminates every active run (and
   * optionally every scheduled rerun) in the same includeSystemManaged scope
   * as the triage reads. Does not walk trees — abandoned children are
   * themselves active rows, so they are included.
   */
  terminateAllActive: oc
    .input(TerminateAllActiveInputSchema)
    .output(TerminateAllActiveOutputSchema)
    .route({
      summary: "Terminate all active workflow runs",
      description:
        "Org-wide emergency stop: terminates every running, queued or idle workflow run with a reason and by default cancels all scheduled reruns. System workflows spared unless included.",
      method: "POST",
    }),
} as const;
